import * as XLSX from 'xlsx'
import type { PerformanceResponse } from '@/types/performance'

export function exportPerformanceExcel(data: PerformanceResponse) {
  const wb = XLSX.utils.book_new()

  // --- Summary Sheet ---
  const summaryRows = data.employees.map(emp => ({
    'Name': emp.name,
    'Email': emp.email,
    'Department': emp.department || '-',
    'Position': emp.position || '-',
    'Days Present': emp.attendance.daysPresent,
    'Attendance %': emp.attendance.attendanceRate,
    'Total Working Hours': emp.attendance.totalWorkingHours,
    'Avg Working Hours': emp.attendance.avgWorkingHours,
    'Tasks Assigned': emp.tasks.assigned,
    'Tasks Completed': emp.tasks.completed,
    'Tasks Pending': emp.tasks.pending,
    'Tasks In Progress': emp.tasks.inProgress,
    'Completion %': emp.tasks.completionRate,
    'Avg Turnaround (days)': emp.tasks.avgTurnaroundDays,
    'Total Hours': emp.dailyWork.totalHours,
    'Avg Hours/Day': emp.dailyWork.avgHoursPerDay,
    'Days Logged': emp.dailyWork.daysWithLogs,
    'Consistency %': emp.dailyWork.consistencyRate,
    'Leave Days': emp.leaveWfh.leaveDays,
    'WFH Days': emp.leaveWfh.wfhDays,
    'Availability %': emp.leaveWfh.availabilityRate,
  }))

  const summarySheet = XLSX.utils.json_to_sheet(summaryRows)

  // Set column widths
  summarySheet['!cols'] = [
    { wch: 20 }, { wch: 25 }, { wch: 15 }, { wch: 15 },
    { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 10 },
    { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 14 },
    { wch: 12 }, { wch: 18 },
    { wch: 11 }, { wch: 13 }, { wch: 11 }, { wch: 13 },
    { wch: 11 }, { wch: 10 }, { wch: 13 },
  ]

  XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary')

  // --- Attendance Detail Sheet ---
  const formatDateDDMMYYYY = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-')
    return `${d}/${m}/${y}`
  }
  const formatTime12hr = (timeStr: string | null) => {
    if (!timeStr) return '-'
    if (/^\d{2}:\d{2}$/.test(timeStr)) {
      const [h, min] = timeStr.split(':').map(Number)
      const period = h >= 12 ? 'PM' : 'AM'
      const hr = h % 12 || 12
      return `${hr}:${String(min).padStart(2, '0')} ${period}`
    }
    return timeStr
  }
  const attendanceRows: any[] = []
  for (const emp of data.employees) {
    const details = data.attendanceDetails[emp.userId] || []
    for (const detail of details) {
      attendanceRows.push({
        'Name': emp.name,
        'Date': formatDateDDMMYYYY(detail.date),
        'Status': detail.status,
        'Check In': formatTime12hr(detail.checkIn),
        'Check Out': formatTime12hr(detail.checkOut),
        'Working Hours': detail.workingHours,
        'Work Mode': detail.workMode || '-',
      })
    }
  }

  if (attendanceRows.length > 0) {
    const attendanceSheet = XLSX.utils.json_to_sheet(attendanceRows)
    attendanceSheet['!cols'] = [
      { wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 10 },
    ]
    XLSX.utils.book_append_sheet(wb, attendanceSheet, 'Attendance Detail')
  }

  // Download
  const startFormatted = formatDateDDMMYYYY(data.dateRange.start).replace(/\//g, '-')
  const endFormatted = formatDateDDMMYYYY(data.dateRange.end).replace(/\//g, '-')
  const fileName = `Performance_Report_${startFormatted}_to_${endFormatted}.xlsx`
  XLSX.writeFile(wb, fileName)
}
